'use client';

import React, { useState } from 'react';
import { Copy, Check, Info } from 'lucide-react';
import { toast } from 'sonner';

interface Credential {
  email: string;
  password: string;
}

interface DemoCredentialsProps {
  credentials: Credential[];
  onFill: (email: string, password: string) => void;
}

export default function DemoCredentials({ credentials, onFill }: DemoCredentialsProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (value: string, field: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    toast.success(`${field} kopiert!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div
      className="mt-5 rounded-xl p-4"
      style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Info size={13} style={{ color: 'var(--neon-violet)' }} />
        <p className="text-xs font-semibold" style={{ color: 'var(--neon-violet)' }}>
          Demo-Zugangsdaten
        </p>
      </div>

      {credentials.map((cred, idx) => (
        <div key={`demo-cred-${idx}`} className="space-y-2">
          {/* Email row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>E-Mail</p>
              <p className="text-xs font-mono font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                {cred.email}
              </p>
            </div>
            <button
              onClick={() => copyToClipboard(cred.email, 'E-Mail')}
              className="p-1.5 rounded-lg transition-colors hover:bg-white/5 flex-shrink-0"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label="E-Mail kopieren"
            >
              {copiedField === 'E-Mail' ? <Check size={13} style={{ color: 'var(--neon-green)' }} /> : <Copy size={13} />}
            </button>
          </div>

          {/* Password row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Passwort</p>
              <p className="text-xs font-mono font-semibold" style={{ color: 'var(--foreground)' }}>
                {cred.password}
              </p>
            </div>
            <button
              onClick={() => copyToClipboard(cred.password, 'Passwort')}
              className="p-1.5 rounded-lg transition-colors hover:bg-white/5 flex-shrink-0"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label="Passwort kopieren"
            >
              {copiedField === 'Passwort' ? <Check size={13} style={{ color: 'var(--neon-green)' }} /> : <Copy size={13} />}
            </button>
          </div>

          {/* Fill button */}
          <button
            onClick={() => onFill(cred.email, cred.password)}
            className="w-full py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 mt-1"
            style={{ background: 'rgba(124,58,237,0.15)', color: 'var(--neon-violet)' }}
          >
            Felder automatisch ausfüllen
          </button>
        </div>
      ))}
    </div>
  );
}